import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { z } from "zod";

import { useCreateTemplate } from "~/features/templates/hooks/use-create-template.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { useUserConfiguration } from "~/shared/hooks/use-user-configuration.ts";
import type { Config } from "~/shared/schemas/config-schema.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type { FreightClass } from "~/shared/schemas/freight-classes-schema.ts";
import { freightClassSchema } from "~/shared/schemas/freight-classes-schema.ts";
import type { QuoteBody } from "~/shared/schemas/quote-schema.ts";
import type {
	TemplateCreateBody,
	TemplateGridItem,
	TemplateResponse,
} from "~/shared/schemas/template-schema.ts";
import { templateResponseSchema } from "~/shared/schemas/template-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	deleteAndValidate,
	deleteAndValidateTraxAr,
	getAndValidate,
	getAndValidateTraxAr,
} from "~/shared/services/api/fetch-client.ts";
import { base64Decode } from "~/shared/utils/base64-utils.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

const templateListSchema = z.array(templateResponseSchema);

const deleteResponseSchema = z.union([
	z.void(),
	z.null(),
	z.string(),
	z.object({}).passthrough(),
]);

export const mapTemplateToGridItem = (
	item: TemplateResponse,
	freightClasses?: FreightClass[],
): TemplateGridItem => {
	let parsedJson: Partial<QuoteBody> | undefined;
	try {
		parsedJson = JSON.parse(
			base64Decode(item.templateJson),
		) as Partial<QuoteBody>;
	} catch (error) {
		console.error(
			"Failed to parse templateJson for template",
			item.templateId,
			error,
		);
	}

	const commodities = parsedJson?.quoteCommodities ?? [];
	const firstCommodity = commodities[0];
	const hasMultipleItems = commodities.length > 1;
	const suffix = hasMultipleItems ? "..." : "";

	const origin = parsedJson?.origin;
	const destination = parsedJson?.destination;
	const pickupDetails = parsedJson?.pickupDetails;
	const deliveryDetails = parsedJson?.deliveryDetails;
	const customer = parsedJson?.customer;

	const pickupLoc = [origin?.city, origin?.state].filter(Boolean).join(", ");
	const pickupLocation = [pickupLoc, origin?.postalCode]
		.filter(Boolean)
		.join(" ");
	const pickupStreet = [pickupDetails?.address1, pickupDetails?.address2]
		.filter(Boolean)
		.join(", ");

	const deliveryLoc = [destination?.city, destination?.state]
		.filter(Boolean)
		.join(", ");
	const deliveryLocation = [deliveryLoc, destination?.postalCode]
		.filter(Boolean)
		.join(" ");
	const deliveryStreet = [deliveryDetails?.address1, deliveryDetails?.address2]
		.filter(Boolean)
		.join(", ");

	return {
		templateId: item.templateId,
		templateName: item.name,
		shipperName: pickupDetails?.stopName ?? "",
		pickupStreet,
		pickupLocation,
		deliveryName: deliveryDetails?.stopName ?? "",
		deliveryStreet,
		deliveryLocation,
		commodity: `${firstCommodity?.description ?? ""}${suffix}`,
		nmfc: `${firstCommodity?.nmfc ?? ""}${suffix}`,
		freightClass: `${freightClasses?.find((fc) => fc.id === firstCommodity?.freightClassId)?.code ?? (firstCommodity?.freightClassId ? String(firstCommodity.freightClassId) : "")}${suffix}`,
		quantity: `${firstCommodity?.quantity?.toString() ?? ""}${suffix}`,
		account: customer?.name ?? "",
		isFavorite: false,
		isDefault: false,
		raw: item,
	};
};

export const useTemplates = (customerId: number | undefined) => {
	const isInternal = getIsInternal();
	const { isVendor } = useIsVendor();
	const searchRoute = isInternal
		? String(apiRoutes.TEMPLATESEARCH)
		: String(apiRoutes.EXTERNALTEMPLATESEARCH);

	return useQuery({
		queryKey: ["templates", customerId],
		queryFn: async (): Promise<TemplateGridItem[]> => {
			if (!customerId) return [];

			const url = `${searchRoute}?customerId=${customerId}&typeEnum=quote`;
			const [result, freightClassResult] = await Promise.all([
				isInternal
					? getAndValidateTraxAr(url, templateListSchema)
					: getAndValidate(url, templateListSchema),
				getAndValidate(apiRoutes.FREIGHTCLASSES, freightClassSchema.array()),
			]);

			if (!result.success) {
				return [];
			}

			const freightClasses = freightClassResult.success
				? freightClassResult.data
				: [];

			return result.data.map((item: TemplateResponse): TemplateGridItem => {
				return mapTemplateToGridItem(item, freightClasses);
			});
		},
		enabled: !!customerId && !isVendor,
	});
};

type UseDeleteTemplateReturn = {
	deleteTemplate: (templateId: number) => Promise<void>;
	isDeleting: boolean;
};

export const useDeleteTemplate = (): UseDeleteTemplateReturn => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();

	const deleteMutation = useMutation({
		mutationFn: async (templateId: number) => {
			const isInternal = getIsInternal();
			const baseRoute = isInternal
				? apiRoutes.DELETETEMPLATE
				: apiRoutes.EXTERNALDELETETEMPLATE;
			const route = baseRoute.replace(":id", String(templateId));

			const result = isInternal
				? await deleteAndValidateTraxAr(route, deleteResponseSchema)
				: await deleteAndValidate(route, deleteResponseSchema);

			if (!result.success) {
				throw new Error(result.error);
			}

			return { templateId };
		},
		onMutate: async (templateId: number) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: ["templates"] });

			// Snapshot the previous value for all matching queries
			const previousData = queryClient.getQueriesData<TemplateGridItem[]>({
				queryKey: ["templates"],
			});

			// Optimistically remove the item from all matching queries
			queryClient.setQueriesData<TemplateGridItem[]>(
				{ queryKey: ["templates"] },
				(old) => old?.filter((item) => item.templateId !== templateId),
			);

			return { previousData };
		},
		onSuccess: () => {
			showSuccess("Template deleted successfully.");
		},
		onError: (
			error,
			_templateId,
			context?: {
				previousData: [unknown, TemplateGridItem[] | undefined][];
			},
		) => {
			// Rollback to the previous value on error
			if (context?.previousData) {
				for (const [queryKey, data] of context.previousData) {
					queryClient.setQueryData(queryKey as unknown[], data);
				}
			}
			console.error(error);
			showError("Failed to delete template. Please try again later.");
		},
		onSettled: () => {
			// Always refetch after error or success to ensure cache is in sync
			void queryClient.invalidateQueries({
				queryKey: ["templates"],
			});
		},
	});

	const { mutateAsync: deleteMutateAsync } = deleteMutation;
	const deleteTemplate = useCallback(
		async (templateId: number): Promise<void> => {
			await deleteMutateAsync(templateId);
		},
		[deleteMutateAsync],
	);

	return {
		deleteTemplate,
		isDeleting: deleteMutation.isPending,
	};
};

export type UseTemplateDefaultReturn = {
	toggleDefault: (
		templateId: number,
		shouldBeDefault: boolean,
	) => Promise<void>;
	isHandlingDefault: boolean;
	userConfigs: Config[];
};

export const useTemplateDefault = (
	customerId: number | undefined,
): UseTemplateDefaultReturn => {
	const { isInternal } = useDeploymentType();
	const { employeeId } = useIsAuthenticated();
	const {
		userConfigs,
		createUserConfig,
		updateUserConfig,
		deleteUserConfig,
		isCreating,
		isUpdating,
		isDeleting,
	} = useUserConfiguration();

	const toggleDefault = useCallback(
		async (templateId: number, shouldBeDefault: boolean) => {
			if (!customerId) return;
			if (isInternal && !employeeId) return;

			const configValue = String(templateId);

			if (shouldBeDefault) {
				// Single default logic: find existing default for this customer
				// userConfigs is already scoped to the current user (employeeId for internal, B2C token for external)
				const existingDefault = userConfigs.find(
					(c) =>
						c.configType === ConfigurationType.QuoteTemplateDefault &&
						c.customerId === customerId,
				);

				// If it's already set to this template, nothing to do
				if (existingDefault?.configValue === configValue) {
					return;
				}

				// If we have an existing default with a real database ID, update or delete it
				// Optimistic IDs are negative - we avoid deleting those to prevent network errors
				if (existingDefault && existingDefault.id > 0) {
					// Using update instead of delete+create is more efficient if the record exists
					await updateUserConfig({
						...existingDefault,
						configValue,
					});
					return;
				}

				// If no existing default or it was optimistic, create a new one
				await createUserConfig({
					id: 0,
					customerId,
					employeeId: isInternal ? Number(employeeId) : null,
					configType: ConfigurationType.QuoteTemplateDefault,
					configValue,
				});
			} else {
				const config = userConfigs.find(
					(c) =>
						c.configType === ConfigurationType.QuoteTemplateDefault &&
						c.customerId === customerId &&
						c.configValue === configValue,
				);
				// Only attempt to delete if it's a real database ID
				if (config && config.id > 0) {
					await deleteUserConfig(config.id);
				}
			}
		},
		[
			customerId,
			isInternal,
			employeeId,
			userConfigs,
			createUserConfig,
			updateUserConfig,
			deleteUserConfig,
		],
	);

	return {
		toggleDefault,
		isHandlingDefault: isCreating || isUpdating || isDeleting,
		userConfigs,
	};
};

type UseDuplicateTemplateReturn = {
	duplicateTemplate: (templateId: number) => Promise<number | undefined>;
	isDuplicating: boolean;
};

export const useDuplicateTemplate = (
	templates: TemplateGridItem[] | undefined,
): UseDuplicateTemplateReturn => {
	const { mutateAsync: createTemplate, isPending: isDuplicating } =
		useCreateTemplate();

	const duplicateTemplate = useCallback(
		async (templateId: number): Promise<number | undefined> => {
			const templateToCopy = templates?.find(
				(t) => t.templateId === templateId,
			);
			if (!templateToCopy) {
				return undefined;
			}

			const copyBody: TemplateCreateBody = {
				name: `${templateToCopy.templateName} - Copy`,
				typeEnum: templateToCopy.raw.typeEnum,
				customerId: templateToCopy.raw.customerId,
				userId: templateToCopy.raw.userId,
				templateJson: templateToCopy.raw.templateJson,
			};

			const result = await createTemplate(copyBody);

			if (result.success) {
				return result.data.templateId;
			}

			return undefined;
		},
		[templates, createTemplate],
	);

	return {
		duplicateTemplate,
		isDuplicating,
	};
};
